import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { createPaymentIntent, analyze } from '../api/services'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)

const CARD_STYLE = {
    style: {
        base: {
            color: '#E6EDF3',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px',
            '::placeholder': { color: '#8B949E' },
            iconColor: '#D4A843',
        },
        invalid: { color: '#f87171' },
    },
}

function CheckoutForm({ message, relationship, plan, isPlanFlow }) {
    const stripe = useStripe()
    const elements = useElements()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [clientSecret, setClientSecret] = useState('')
    const [intentReady, setIntentReady] = useState(false)
    const [initError, setInitError] = useState('')

    useEffect(() => {
        // Always use diagnose_rewrite PaymentIntent for the demo
        // This demonstrates real Stripe card collection end-to-end
        createPaymentIntent('diagnose_rewrite')
            .then((res) => {
                setClientSecret(res.data.client_secret)
                setIntentReady(true)
            })
            .catch((err) => {
                const detail = err.response?.data?.detail
                setInitError(
                    typeof detail === 'string'
                        ? detail
                        : 'Failed to initialise payment. Please try again.'
                )
            })
    }, [])

    const handlePay = async (e) => {
        e.preventDefault()
        if (!stripe || !elements || !clientSecret) return

        setError('')
        setLoading(true)

        try {
            const card = elements.getElement(CardElement)
            const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
                clientSecret,
                { payment_method: { card } },
            )

            if (stripeError) {
                setError(stripeError.message)
                setLoading(false)
                return
            }

            if (paymentIntent.status === 'succeeded') {
                if (isPlanFlow) {
                    // Plan selection flow — payment confirmed, go to analyze
                    // In full M4 the webhook will update plan_tier on the user
                    navigate('/analyze', {
                        state: { planActivated: plan?.display_name }
                    })
                } else {
                    // PAYG flow — run analysis immediately with payment_intent_id
                    const res = await analyze({
                        message,
                        relationship,
                        payment_intent_id: paymentIntent.id,
                    })
                    navigate('/results', { state: { result: res.data } })
                }
            }
        } catch (err) {
            const detail = err.response?.data?.detail
            setError(
                typeof detail === 'string'
                    ? detail
                    : detail?.message || 'Payment failed. Please try again.'
            )
        } finally {
            setLoading(false)
        }
    }

    if (initError) {
        return (
            <div className="border border-red-500/30 bg-red-500/10 rounded px-4 py-3">
                <p className="text-red-400 text-sm font-body">{initError}</p>
            </div>
        )
    }

    if (!intentReady) {
        return (
            <div className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4 text-brand-amber" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <p className="text-brand-muted text-sm font-body">Initialising payment…</p>
            </div>
        )
    }

    return (
        <form onSubmit={handlePay} className="space-y-6">
            <div>
                <label className="label">Card details</label>
                <div className="input-field">
                    <CardElement options={CARD_STYLE} />
                </div>
                <p className="text-brand-muted text-xs font-body mt-2">
                    Test card: 4242 4242 4242 4242 · Any future date · Any CVC
                </p>
            </div>

            {error && (
                <div className="border border-red-500/30 bg-red-500/10 rounded px-4 py-3">
                    <p className="text-red-400 text-sm font-body">{error}</p>
                </div>
            )}

            <button
                type="submit"
                disabled={!stripe || !intentReady || loading}
                className="btn-primary w-full text-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {loading ? (
                    <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10"
                                stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Processing…
                    </span>
                ) : isPlanFlow ? (
                    `Pay £${(plan?.price_pence / 100).toFixed(2)}/mo`
                ) : (
                    'Pay £1.99 and analyse'
                )}
            </button>
        </form>
    )
}

export default function Pay() {
    const { state } = useLocation()
    const navigate = useNavigate()

    const plan = state?.plan || null
    const message = state?.message || ''
    const relationship = state?.relationship || ''

    // Plan flow: came from Plans page with a plan selected
    const isPlanFlow = !!plan && !message

    // Neither plan nor message — invalid state
    if (!plan && !message) {
        return (
            <div className="min-h-screen bg-brand-navy flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-brand-muted font-body text-sm mb-4">
                            No payment context found. Please start from the analysis page.
                        </p>
                        <button onClick={() => navigate('/analyze')} className="btn-primary">
                            Go to Analyse
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        )
    }

    const orderLabel = isPlanFlow
        ? `${plan.display_name} plan — first month`
        : 'Message analysis — full risk assessment'

    const orderPrice = isPlanFlow
        ? `£${(plan.price_pence / 100).toFixed(2)}/mo`
        : '£1.99'

    return (
        <div className="min-h-screen bg-brand-navy flex flex-col">
            <Navbar />
            <div className="flex-1 max-w-lg mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full">

                <div className="mb-10">
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-brand-text mb-3">
                        Complete payment
                    </h1>
                    <p className="text-brand-muted font-body text-sm">
                        {isPlanFlow
                            ? `Activate your ${plan.display_name} plan.`
                            : 'One-time payment to analyse this message.'}
                    </p>
                </div>

                {/* Order summary */}
                <div className="card mb-8">
                    <p className="text-brand-muted text-xs font-body uppercase tracking-widest mb-4">
                        Order summary
                    </p>
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <p className="text-brand-text font-body text-sm font-medium">
                                {orderLabel}
                            </p>
                            {isPlanFlow && (
                                <p className="text-brand-muted text-xs font-body mt-0.5">
                                    {plan.monthly_cap} analyses per month
                                </p>
                            )}
                        </div>
                        <span className="text-brand-amber font-display font-semibold shrink-0 ml-4">
                            {orderPrice}
                        </span>
                    </div>
                    <div className="border-t border-brand-border pt-4 flex items-center justify-between">
                        <span className="text-brand-text font-body text-sm font-medium">Total today</span>
                        <span className="text-brand-amber font-display font-bold text-lg">
                            {isPlanFlow ? `£${(plan.price_pence / 100).toFixed(2)}` : '£1.99'}
                        </span>
                    </div>
                </div>

                <Elements stripe={stripePromise}>
                    <CheckoutForm
                        message={message}
                        relationship={relationship}
                        plan={plan}
                        isPlanFlow={isPlanFlow}
                    />
                </Elements>

                <div className="mt-6 flex items-center justify-center gap-2">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M6 1L11 3.5V6C11 8.8 8.8 10.9 6 11C3.2 10.9 1 8.8 1 6V3.5L6 1Z"
                            stroke="#8B949E" strokeWidth="1" />
                    </svg>
                    <p className="text-brand-muted text-xs font-body">
                        Secured by Stripe · Your message is never stored
                    </p>
                </div>
            </div>
            <Footer />
        </div>
    )
}